/**
 * Firearm acquisition request page.
 * Walks users through sourcing steps and collects contact details.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText,
  PhoneCall,
  Search,
  CheckCircle,
  Send,
  User,
  Mail,
  Phone,
  MapPin,
  Crosshair
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { isApiConfigured, getMyProfile, submitAcquisitionRequest } from '../services/apiClient';
import { setSEO } from '../utils/seo';

const STEPS = [
  {
    number: '01',
    title: 'Submit a Request',
    description: 'Tell us the firearm you want — type, make, model, caliber, condition preference, and your price range. Include your contact details so we can reach you.',
    icon: FileText,
  },
  {
    number: '02',
    title: 'We Reach Out',
    description: 'Our team contacts you to confirm the details, answer questions, and lock in exactly what you are looking for before sourcing begins.',
    icon: PhoneCall,
  },
  {
    number: '03',
    title: 'We Source It',
    description: 'We search our dealer network for the match — from everyday carry pistols to hard-to-find collector pieces — so you do not have to hunt it down yourself.',
    icon: Search,
  },
  {
    number: '04',
    title: 'Payment & Transfer',
    description: 'When we find it, we finalize payment and arrange a legal FFL transfer. No deposits or sourcing fees to get started.',
    icon: CheckCircle,
  },
];

const emptyForm = {
  fullName: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  firearmDetails: '',
};

const Acquisition = () => {
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedItem = searchParams.get('item') || '';
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSEO({
      title: 'Acquire a Firearm | Tolbert Tactical',
      description: 'Cannot find the firearm you want? Submit a sourcing request and our team will track it down and arrange a legal FFL transfer.',
      path: '/acquisition',
    });
  }, []);

  useEffect(() => {
    if (requestedItem) {
      setForm((prev) => ({
        ...prev,
        firearmDetails: prev.firearmDetails || `Request to purchase: ${requestedItem}`,
      }));
    }
  }, [requestedItem]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated || !user) return;

      const next = {
        fullName: user.name || user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
      };

      if (isApiConfigured()) {
        try {
          const profile = await getMyProfile();
          const shipping = profile?.shippingAddress || {};
          Object.assign(next, {
            fullName: next.fullName || profile?.name || '',
            phone: next.phone || profile?.phone || '',
            line1: shipping.line1 || '',
            line2: shipping.line2 || '',
            city: shipping.city || '',
            state: shipping.state || '',
            postalCode: shipping.postalCode || '',
          });
        } catch (error) {
          console.warn('Could not prefill acquisition form from profile:', error);
        }
      }

      if (!cancelled) {
        setForm((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.fullName.trim()) nextErrors.fullName = 'Full name is required';
    if (!form.email.trim()) nextErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = 'Enter a valid email';
    if (!form.phone.trim()) nextErrors.phone = 'Phone number is required';
    if (!form.line1.trim()) nextErrors.line1 = 'Street address is required';
    if (!form.city.trim()) nextErrors.city = 'City is required';
    if (!form.state.trim()) nextErrors.state = 'State is required';
    if (!form.postalCode.trim()) nextErrors.postalCode = 'ZIP code is required';
    if (!form.firearmDetails.trim()) nextErrors.firearmDetails = 'Tell us what you are looking for';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please complete the required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: {
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          postalCode: form.postalCode.trim(),
          country: 'US',
        },
        firearmDetails: form.firearmDetails.trim(),
        sourceGiveaway: requestedItem || null,
      };

      if (isApiConfigured()) {
        const response = await submitAcquisitionRequest(payload);
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to submit request');
        }
      } else {
        console.log('Acquisition request (dev mode):', payload);
      }

      setSubmitted(true);
      toast.success('Request submitted. Our team will reach out shortly.');
    } catch (error) {
      console.error('Acquisition request failed:', error);
      toast.error(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-black">
      <section className="relative overflow-hidden border-b border-theme-bronze/20">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-custom-orange text-xs font-bold tracking-[0.3em] uppercase mb-4">
            // Find My Gun
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-theme-silver uppercase tracking-tight mb-4">
            Can&apos;t Find It? We&apos;ll Source It.
          </h1>
          <p className="text-lg text-theme-silver-light max-w-2xl">
            Tell us exactly what you&apos;re looking for. Our sourcing team tracks down hard-to-find firearms — from everyday carry pistols to rare collector pieces — so you don&apos;t have to.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-custom-orange text-xs font-bold tracking-[0.3em] uppercase mb-6">
          // How It Works
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="glass-card-dark p-6 flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-lg border border-custom-orange/50 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-custom-orange" />
                  </div>
                  <p className="text-custom-orange text-xs font-bold mt-2 tracking-widest">{step.number}</p>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-theme-silver mb-2">{step.title}</h2>
                  <p className="text-sm text-theme-silver-light leading-relaxed">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section id="request-form" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="glass-card-dark p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Crosshair className="h-6 w-6 text-custom-orange" />
            <h2 className="text-2xl font-bold text-theme-silver">Submit a Request</h2>
          </div>

          {submitted ? (
            <div className="text-center py-10">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <Send className="h-7 w-7 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-theme-silver mb-2">Request received</h3>
              <p className="text-theme-silver-light max-w-md mx-auto mb-6">
                Our team will reach out using the contact information you provided to confirm details and start sourcing.
              </p>
              <Link to="/giveaways" className="btn-secondary inline-block">
                Back to Giveaways
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-silver-light" />
                    <input
                      type="text"
                      value={form.fullName}
                      onChange={(e) => updateField('fullName', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver ${errors.fullName ? 'border-red-500' : ''}`}
                      placeholder="Jane Doe"
                      autoComplete="name"
                    />
                  </div>
                  {errors.fullName && <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-silver-light" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="you@email.com"
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-silver-light" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="(555) 555-5555"
                      autoComplete="tel"
                    />
                  </div>
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">Street Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-silver-light" />
                    <input
                      type="text"
                      value={form.line1}
                      onChange={(e) => updateField('line1', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 glass-input text-theme-silver ${errors.line1 ? 'border-red-500' : ''}`}
                      placeholder="123 Main St"
                      autoComplete="address-line1"
                    />
                  </div>
                  {errors.line1 && <p className="text-red-400 text-xs mt-1">{errors.line1}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">Address Line 2 (optional)</label>
                  <input
                    type="text"
                    value={form.line2}
                    onChange={(e) => updateField('line2', e.target.value)}
                    className="w-full px-4 py-3 glass-input text-theme-silver"
                    placeholder="Apt, suite, etc."
                    autoComplete="address-line2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-silver-light mb-2">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    className={`w-full px-4 py-3 glass-input text-theme-silver ${errors.city ? 'border-red-500' : ''}`}
                    placeholder="City"
                    autoComplete="address-level2"
                  />
                  {errors.city && <p className="text-red-400 text-xs mt-1">{errors.city}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">State</label>
                    <input
                      type="text"
                      value={form.state}
                      onChange={(e) => updateField('state', e.target.value)}
                      className={`w-full px-4 py-3 glass-input text-theme-silver ${errors.state ? 'border-red-500' : ''}`}
                      placeholder="UT"
                      autoComplete="address-level1"
                    />
                    {errors.state && <p className="text-red-400 text-xs mt-1">{errors.state}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">ZIP</label>
                    <input
                      type="text"
                      value={form.postalCode}
                      onChange={(e) => updateField('postalCode', e.target.value)}
                      className={`w-full px-4 py-3 glass-input text-theme-silver ${errors.postalCode ? 'border-red-500' : ''}`}
                      placeholder="84000"
                      autoComplete="postal-code"
                    />
                    {errors.postalCode && <p className="text-red-400 text-xs mt-1">{errors.postalCode}</p>}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-silver-light mb-2">What firearm are you looking for?</label>
                <textarea
                  value={form.firearmDetails}
                  onChange={(e) => updateField('firearmDetails', e.target.value)}
                  className={`w-full px-4 py-3 glass-input text-theme-silver h-32 resize-none ${errors.firearmDetails ? 'border-red-500' : ''}`}
                  placeholder="Type, make, model, caliber, condition preference, and price range"
                  maxLength={2000}
                />
                {errors.firearmDetails && <p className="text-red-400 text-xs mt-1">{errors.firearmDetails}</p>}
              </div>

              <p className="text-xs text-theme-silver-light">
                Transfers are facilitated nationwide through our Utah-licensed FFL. You must be legally eligible to possess a firearm and comply with all applicable federal, state, and local laws.
              </p>

              <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Send className="h-4 w-4" />
                {submitting ? 'Submitting…' : 'Submit a Request'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Acquisition;
